import { useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { BankIcon } from 'foundations-assets/icons/components/BankIcon';
import { CreditCardIcon } from 'foundations-assets/icons/components/CreditCardIcon';
import { EllipsesIcon } from 'foundations-assets/icons/components/EllipsesIcon';
import { formattedPaymentMethodLabel } from 'commerce-shared-components-lib/features/checkout/utils/formattedPaymentMethodLabel';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import { GenericLoadingState } from '../components/GenericLoadingState';
import { RemovePaymentMethodModal } from '../components/RemovePaymentMethodModal';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { PAYMENT_METHOD_TYPE } from '../utils/typeAdapters';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function PaymentMethodsCard({
  paymentMethods = [],
  loading
}) {
  if (loading) {
    return /*#__PURE__*/_jsx(GenericLoadingState, {});
  }
  if (!paymentMethods.length) {
    return /*#__PURE__*/_jsx(EmptyState, {
      flush: true,
      layout: "vertical",
      primaryContent: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "paymentMethods.emptyStateMessage"
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "300",
    children: [paymentMethods.map(paymentMethod => /*#__PURE__*/_jsx(PaymentMethodTile, {
      paymentMethod: paymentMethod,
      isOnlyPaymentMethod: paymentMethods.length === 1
    }, paymentMethod.objectId)), paymentMethods.length % 2 !== 0 && /*#__PURE__*/_jsx(Flex, {
      minInlineSize: "280px",
      maxInlineSize: "425px",
      flexBasis: "calc(50% - 12px)",
      flexGrow: 1,
      "aria-hidden": true
    })]
  });
}
function ActionsMenuButton(props) {
  return /*#__PURE__*/_jsx(IconButton, Object.assign({}, props, {
    use: "secondary-ghost",
    size: "extra-small",
    "aria-label": I18n.text('paymentMethods.moreActions'),
    "data-test-id": "payment-method-actions-menu",
    children: /*#__PURE__*/_jsx(EllipsesIcon, {})
  }));
}
export function PaymentMethodTile({
  paymentMethod,
  isOnlyPaymentMethod
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    type,
    last4,
    brand,
    expMonth,
    expYear
  } = paymentMethod.paymentMethodDetails;
  const {
    storedOn
  } = paymentMethod;
  const isCard = type === PAYMENT_METHOD_TYPE.CARD;
  const hasExpiration = isCard && !!expMonth && !!expYear;
  const isPill = useIsPillBorderRadius();
  const paymentMethodLabel = formattedPaymentMethodLabel(readUnhandledEnumCase(type !== null && type !== void 0 ? type : ''), brand !== null && brand !== void 0 ? brand : undefined, last4 !== null && last4 !== void 0 ? last4 : undefined);
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    borderRadius: isPill ? '0' : '200',
    overflow: "hidden",
    borderWidth: "100",
    borderColor: "border-core-subtle",
    borderStyle: "solid",
    backgroundColor: "fill-surface-default",
    minInlineSize: "280px",
    maxInlineSize: "425px",
    flexBasis: "calc(50% - 12px)",
    flexGrow: 1,
    paddingInline: "700",
    paddingBlock: "400",
    "data-test-id": "payment-method-tile",
    children: [/*#__PURE__*/_jsxs(Flex, {
      justifyContent: "space-between",
      alignItems: "start",
      children: [/*#__PURE__*/_jsxs(View, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          marginBlockEnd: "200",
          children: [/*#__PURE__*/_jsx(StatusDot, {
            use: "info"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "detail-200",
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "paymentMethods.storedOn",
              options: {
                date: /*#__PURE__*/_jsx(FormattedDateTime, {
                  value: storedOn,
                  format: "LL"
                })
              }
            })
          })]
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          columnGap: "200",
          marginBlockEnd: "100",
          children: [isCard ? /*#__PURE__*/_jsx(CreditCardIcon, {
            size: "400"
          }) : /*#__PURE__*/_jsx(BankIcon, {
            size: "400"
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-200",
            children: paymentMethodLabel
          })]
        }), hasExpiration && /*#__PURE__*/_jsx(Text, {
          typography: "detail-100",
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "paymentMethods.expires",
            options: {
              date: /*#__PURE__*/_jsx(FormattedDateTime, {
                value: new Date(expYear, expMonth - 1),
                format: "MMMM YYYY"
              })
            }
          })
        })]
      }), /*#__PURE__*/_jsx(Dropdown, {
        Button: ActionsMenuButton,
        children: /*#__PURE__*/_jsx(List, {
          children: /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(Tooltip, {
              disabled: !isOnlyPaymentMethod,
              title: I18n.text('paymentMethods.removeDisabledTooltip'),
              children: /*#__PURE__*/_jsx(Button, {
                "data-test-id": "remove-payment-method-button",
                onClick: () => setShowDeleteModal(true),
                disabled: isOnlyPaymentMethod,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "paymentMethods.removeStoredPaymentMethod"
                })
              })
            })
          })
        })
      })]
    }), showDeleteModal && /*#__PURE__*/_jsx(RemovePaymentMethodModal, {
      paymentMethod: paymentMethod,
      onClose: () => setShowDeleteModal(false)
    })]
  });
}